import {
  zonedParts,
  zonedWallTimeToUtc,
} from '@kirin/api-client/helpers/time-zone';
import { resolveTimeZone } from '@/lib/displayTimeZone';
import { RELATIVE_PRESETS, rangeDays } from './constants';
import type { DisplayTimeZone, RelativePreset, TimeRange } from './types';

export const getRangeByKey = (key: RelativePreset) =>
  RELATIVE_PRESETS.find(p => p.key === key) ?? null;

export function pad2(n: number) {
  return n.toString().padStart(2, '0');
}

/**
 * yyyy-mm-ddThh:mm string for datetime-local in a given tz (or UTC)
 */
export function formatDateTimeInZone(
  date: Date | undefined,
  tz: DisplayTimeZone | undefined
) {
  if (!date || !tz) return '';
  const p = zonedParts(resolveTimeZone(tz), date);
  return `${p.year}-${pad2(p.month)}-${pad2(p.day)}T${pad2(p.hour)}:${pad2(
    p.minute
  )}`;
}

/**
 * UTC label string: yyyy-mm-ddThh:mmZ (always UTC)
 */
export function formatUtcLabel(date: Date | undefined) {
  if (!date) return '';
  return `${date.getUTCFullYear()}-${pad2(date.getUTCMonth() + 1)}-${pad2(
    date.getUTCDate()
  )}T${pad2(date.getUTCHours())}:${pad2(date.getUTCMinutes())}Z`;
}

/**
 * Parse a datetime-local value ("yyyy-mm-ddThh:mm") as wall-clock time in
 * `tz`, or null when it isn't one. A time a DST change skips moves forward by
 * the gap; one it repeats resolves to the first occurrence.
 */
export function parseDateTimeInZone(
  value: string,
  tz: DisplayTimeZone
): Date | null {
  const [datePart, timePart] = value.split('T');
  if (!datePart || !timePart) return null;

  const [y, m, d] = datePart.split('-').map(Number);
  const [hh, mm] = timePart.split(':').map(Number);
  if (![y, m, d, hh, mm].every(Number.isFinite)) return null;

  return zonedWallTimeToUtc(resolveTimeZone(tz), y, m, d, hh, mm);
}

export const getDefaultTimeRange = (tz?: string): TimeRange => {
  const { from, to } = rangeDays(7, tz);
  return { mode: 'relative', preset: '7d', from, to };
};

/**
 * Filter relative presets to only include those whose computed "from" date
 * is at or after the given minDate. This ensures users cannot select time
 * ranges that would extend before the organization was created.
 *
 * Uses exact timestamp comparison so the filter is consistent with the
 * apply-time validation in the TimeRangeSelector component.
 *
 * @param minDate - The earliest date allowed (e.g., organization createdAt)
 * @returns Filtered array of available presets
 */
export function getAvailablePresets(
  minDate: Date | undefined,
  tz?: string
): typeof RELATIVE_PRESETS {
  if (!minDate) return RELATIVE_PRESETS;

  return RELATIVE_PRESETS.filter(preset => {
    const { from } = preset.getRange(tz);
    return from >= minDate;
  });
}
